import { Logger, SharedModule } from '@aiwyn/shared';
import { APP_BASE_HREF, CommonModule, DOCUMENT } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { ChangeDetectionStrategy, Component, Input, OnInit, ViewChild, computed, inject, signal } from '@angular/core';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { Router } from '@angular/router';
import { SafeDatePipe } from '../../../../shared/pipes/safe-date/safe-date.pipe';
import { EsignSigningContextResponse } from '../../models';
import { EsignSigningService } from '../../services';
import { EsignSignerRosterComponent } from './components/esign-signer-roster/esign-signer-roster.component';
import { EsignFieldsProgress, EsignSigningViewerComponent, EsignSubmitPayload } from './components/esign-signing-viewer/esign-signing-viewer.component';

type EsignSigningViewState = 'loading' | 'error' | 'hub' | 'viewer-only' | 'signing' | 'completed';

interface EsignSigningContextError {
  message: string;
  retriable: boolean;
}

/**
 * Secure-link signing view (no top header, no left sidebar) for a recipient who arrives via the
 * eSign secure link. Owns the hub → signing-view flow, the signing-context fetch, and submit;
 * all Apryse/WebViewer work is delegated to {@link EsignSigningViewerComponent}.
 */
@Component({
  selector: 'app-esign-signing-container',
  standalone: true,
  imports: [CommonModule, SharedModule, MatProgressSpinnerModule, SafeDatePipe, EsignSigningViewerComponent, EsignSignerRosterComponent],
  templateUrl: './esign-signing-container.component.html',
  styleUrl: './esign-signing-container.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EsignSigningContainerComponent implements OnInit {
  /**
   * Which bundle to sign. Null on the secure-link surface, where the link names
   * exactly one recipient row; set by the portal, where the same contact can be
   * a recipient on several and the endpoints cannot guess which is meant.
   */
  @Input() bundleId: number | null = null;

  /**
   * Which of the caller's own signer rows on {@link bundleId} this session is acting
   * as, for a bundle that addresses the same person twice. Null on the secure-link
   * surface, and for a caller holding a single row, where the backend resolves it.
   */
  @Input() recipientId: number | null = null;

  /**
   * Where "Return to Portal" goes. Null on the secure-link surface, whose signer is a logged-out
   * guest with no portal to return to — they get the app root, as before. The portal passes the
   * request the signer came from, so the button lands them back where they started rather than at
   * the top of the app.
   */
  @Input() returnTo: unknown[] | null = null;

  @ViewChild(EsignSigningViewerComponent) private readonly _viewerComponent?: EsignSigningViewerComponent;

  private readonly _esignSigningService = inject(EsignSigningService);
  private readonly _router = inject(Router);
  private readonly _logger = inject(Logger);

  protected readonly state = signal<EsignSigningViewState>('loading');
  protected readonly signingContext = signal<EsignSigningContextResponse | null>(null);
  protected readonly contextError = signal<EsignSigningContextError | null>(null);
  protected readonly consentError = signal<string | null>(null);
  protected readonly submitError = signal<string | null>(null);
  protected readonly acceptingConsent = signal(false);
  protected readonly submitting = signal(false);
  protected readonly fieldsProgress = signal<EsignFieldsProgress>({ completed: 0, total: 0 });
  /**
   * True once the signer has entered the signing view at least once. Keeps
   * {@link EsignSigningViewerComponent} mounted (shown/hidden via CSS, not `@switch`) across a
   * Back → Get Started round trip — destroying and re-acquiring it would drop whatever the signer
   * had already filled in, since those annotations only ever live in the WebViewer session.
   */
  protected readonly enteredSigning = signal(false);
  /**
   * True when the signer reached `completed` by submitting in this session, false when they landed
   * on it straight from the entry guard. This is the axis the Figma calls Confirmation (4.x —
   * "Document signed") vs Completion (5.x — "You've already signed"); the other axis is
   * {@link allSigned}. It cannot be derived from the context, because a signer who submits and a
   * signer who returns afterwards both read back as SIGNED.
   */
  protected readonly justSigned = signal(false);
  /** Set when the completion screen's "View Document" is open, over the top of `completed`. */
  protected readonly viewingDocument = signal(false);

  /**
   * `bundleComplete` as reported by the submit response. Trusted over the roster for the signer who
   * just submitted: that answer is computed under a row lock server-side, whereas a context refetch
   * racing the same commit can still read the caller's own row as unsigned.
   */
  private readonly _bundleCompleteFromSubmit = signal(false);

  protected readonly signers = computed(() => this.signingContext()?.signers ?? []);
  protected readonly signedFileUrl = computed(() => this.signingContext()?.file.signedFileUrl ?? null);
  protected readonly signedAt = computed(() => this.signingContext()?.recipient.signedAndAcceptedAt ?? null);

  /**
   * Whether every signature is in — the axis the Figma splits "Pending Signature" from "All
   * Recipients Signed" on. Drives copy only.
   */
  protected readonly allSigned = computed(() => {
    if (this._bundleCompleteFromSubmit()) {
      return true;
    }
    if (this.signingContext()?.bundle.status === 'COMPLETE') {
      return true;
    }
    // Via signers(), never context.signers — an artengine without ART-8784 omits the field, and
    // `undefined.length` here would take the whole completion screen down.
    const signers = this.signers();
    return signers.length > 0 && signers.every(({ status }) => status === 'SIGNED');
  });

  /**
   * Deliberately not the same question as {@link allSigned}. The final PDF is built asynchronously
   * after the last signer commits, so there is a window where the bundle is complete but the file
   * does not exist yet. Gating the button on the URL keeps it from pointing at nothing; the
   * all-signed copy still renders, and a reload once finalization lands reveals the download.
   */
  protected readonly canDownload = computed(() => this.signedFileUrl() != null);
  /** The Figma greets the signer by first name — "Thank you, Polly." */
  protected readonly firstName = computed(() => this.signingContext()?.recipient.displayName.trim().split(/\s+/)[0] ?? '');

  private readonly _document = inject(DOCUMENT);
  private readonly _baseHref = inject(APP_BASE_HREF, { optional: true }) ?? '/';

  /**
   * Bumped on {@link retry} to re-trigger the document load in the mounted
   * {@link EsignSigningViewerComponent}. Without it, a viewer that failed to load its document
   * stays frozen on its own error overlay for the rest of the session: it survives the
   * hub ↔ signing toggle by design, so nothing else ever re-runs its load.
   */
  protected readonly viewerReloadToken = signal(0);

  ngOnInit(): void {
    this._fetchContext();
  }

  protected isReadyToSubmit(): boolean {
    const { completed, total } = this.fieldsProgress();
    return total > 0 && completed >= total;
  }

  protected retry(): void {
    // Bump before the fetch so the viewer picks up the reload in the same change-detection pass
    // that applies the refreshed context — including a newly signed `originalFileUrl`.
    this.viewerReloadToken.update(token => token + 1);
    this._fetchContext();
  }

  protected viewDocument(): void {
    this.viewingDocument.set(true);
  }

  protected backToCompletion(): void {
    this.viewingDocument.set(false);
  }

  /**
   * A full navigation rather than a router hop: the recipient arrived on a secure-link session with
   * no portal identity, so the portal has to bootstrap its own auth from scratch. Routing in-app
   * would land them inside a shell this session cannot populate.
   */
  protected returnToPortal(): void {
    const target = this.returnTo;
    if (target !== null) {
      void this._router.navigate(target);
      return;
    }
    this._document.location.href = this._baseHref;
  }

  protected downloadSignedCopy(): void {
    const url = this.signedFileUrl();
    if (url == null) {
      return;
    }
    this._document.defaultView?.open(url, '_blank', 'noopener,noreferrer');
  }

  protected getStarted(): void {
    this.consentError.set(null);
    this.acceptingConsent.set(true);

    this._esignSigningService.acceptConsent(this.bundleId, this.recipientId).subscribe({
      next: () => {
        this.acceptingConsent.set(false);
        this.enteredSigning.set(true);
        this.state.set('signing');
      },
      error: (err: HttpErrorResponse) => {
        this.acceptingConsent.set(false);
        this._logger.error('EsignSigningContainerComponent :: consent failed', err);
        this.consentError.set('Something went wrong. Please try again.');
      },
    });
  }

  protected backToHub(): void {
    this.state.set('hub');
  }

  protected onFieldsChanged(progress: EsignFieldsProgress): void {
    this.fieldsProgress.set(progress);
  }

  protected onViewerLoadError(): void {
    this.state.set('error');
    this.contextError.set({ message: 'We couldn’t load this document. Please try again.', retriable: true });
  }

  protected onExportError(): void {
    this.submitting.set(false);
    this.submitError.set('Something went wrong submitting your signature. Please try again.');
  }

  protected onSubmitClick(): void {
    if (!this.isReadyToSubmit() || this.submitting()) {
      return;
    }
    this.submitting.set(true);
    this.submitError.set(null);
    this._viewerComponent?.requestSubmit();
  }

  protected onSubmitReady(payload: EsignSubmitPayload): void {
    this._esignSigningService.submitSignature(payload, this.bundleId, this.recipientId).subscribe({
      next: response => {
        this.submitting.set(false);
        this._bundleCompleteFromSubmit.set(response.bundleComplete);
        this._enterCompleted({ justSigned: true });
      },
      error: (err: HttpErrorResponse) => {
        this.submitting.set(false);
        this._logger.error('EsignSigningContainerComponent :: submit failed', err);

        if (err.status === 422) {
          // Already signed, or the bundle stopped awaiting signatures. Not this session's doing, so
          // this is the "you've already signed" screen, not the "document signed" confirmation.
          this._enterCompleted({ justSigned: false });
          return;
        }
        if (err.status === 403) {
          this.state.set('error');
          this.contextError.set({ message: 'This signing link is no longer valid.', retriable: false });
          return;
        }
        this.submitError.set('Something went wrong submitting your signature. Please try again.');
      },
    });
  }

  private _fetchContext(): void {
    this.state.set('loading');
    this.contextError.set(null);

    this._esignSigningService.getSigningContext(this.bundleId, this.recipientId).subscribe({
      next: context => {
        this.signingContext.set(context);
        this._applyGuards(context);
      },
      error: (err: HttpErrorResponse) => {
        this._logger.error('EsignSigningContainerComponent :: failed to load signing context', err);
        this.state.set('error');
        this.contextError.set({
          message: err.status === 403
            ? 'This signing link is no longer valid.'
            : 'We couldn’t load this document. Please try again.',
          retriable: err.status !== 403,
        });
      },
    });
  }

  private _applyGuards(context: EsignSigningContextResponse): void {
    const { recipient } = context;

    if (recipient.status === 'SIGNED' || recipient.signedAndAcceptedAt != null) {
      // Reached the link already signed, so this is Completion (5.x), not Confirmation (4.x). No
      // refresh: the context driving this decision was fetched a moment ago.
      this.justSigned.set(false);
      this.viewingDocument.set(false);
      this.state.set('completed');
      return;
    }
    if (recipient.recipientType === 'VIEWER') {
      this.state.set('viewer-only');
      return;
    }
    this.state.set('hub');
  }

  /**
   * The roster and `signedFileUrl` both change as a side effect of signing, and neither is carried
   * on the submit response, so the completion screen refetches rather than rendering the context it
   * loaded before the signature existed. The refetch is silent — it writes the context in place
   * instead of going through `_fetchContext`, which would drop back to the loading spinner and
   * flash the completed screen away as it returns.
   */
  private _enterCompleted({ justSigned }: { justSigned: boolean }): void {
    this.justSigned.set(justSigned);
    this.viewingDocument.set(false);
    this.state.set('completed');

    this._esignSigningService.getSigningContext(this.bundleId, this.recipientId).subscribe({
      next: context => this.signingContext.set(context),
      // The completed screen already renders from the context in hand; a failed refresh costs an
      // up-to-date roster and the download button, not the confirmation itself.
      error: (err: HttpErrorResponse) =>
        this._logger.error('EsignSigningContainerComponent :: completion refresh failed', err),
    });
  }
}
